/**
 * Shared record types. Everything stored in IndexedDB is described here.
 * Amounts are integers in öre. Dates are ISO `YYYY-MM-DD` strings.
 *
 * Schema v2 adds company settings, payment sources, the plain-language
 * transaction model (`VerifikationMeta`), the inbox, bank import, merchant
 * memory, period locks and Drive state. See docs/shared-contracts.md.
 */
import type { Ore } from './money';

export type IsoDate = string; // YYYY-MM-DD
export type IsoDateTime = string; // full ISO 8601 timestamp
export type Uuid = string;

export const ENTITY_TYPES = [
  'aktiebolag',
  'enskild_firma',
  'ideell_forening',
  'bostadsrattsforening',
] as const;
export type EntityType = (typeof ENTITY_TYPES)[number];

export interface Company {
  id: Uuid;
  name: string;
  /** Organisationsnummer, digits only, or empty for e.g. a brand new förening. */
  orgNumber: string;
  entityType: EntityType;
  createdAt: IsoDateTime;
  /**
   * When name or org.nr were last edited. Lets two devices sharing one
   * bookkeeping agree on which edit is the newer one (see
   * `src/cloud/sync/merge.ts`). Absent on rows written before the field
   * existed, which the merge treats as older than any dated edit.
   */
  updatedAt?: IsoDateTime;
}

export type FiscalYearStatus = 'open' | 'closed';

export interface FiscalYear {
  id: Uuid;
  companyId: Uuid;
  startDate: IsoDate;
  endDate: IsoDate;
  status: FiscalYearStatus;
  /** Human label such as "2026" or "2025/2026". */
  label: string;
}

// ---- Company settings -------------------------------------------------------

export type VatPeriod = 'month' | 'quarter' | 'year';
/** kontantmetoden (bokslutsmetoden) | faktureringsmetoden */
export type AccountingMethod = 'cash' | 'invoice';

export interface CompanySettings {
  /** Equal to `companyId`; one row per company. */
  id: Uuid;
  companyId: Uuid;
  vatRegistered: boolean;
  vatPeriod: VatPeriod;
  accountingMethod: AccountingMethod;
  /** Default 1_000_000 (10 000 kr); amounts above 5_000_000 are always `needs_you`. */
  batchThresholdOre: Ore;
  defaultPaymentSourceId: Uuid | null;
  /** BRF only: 3910 (default) or 3020. */
  arsavgiftAccount?: number;
  onboarding: {
    completedAt: IsoDateTime | null;
    driveSkippedAt: IsoDateTime | null;
    aiSkippedAt: IsoDateTime | null;
  };
  updatedAt: IsoDateTime;
}

// ---- Payment sources --------------------------------------------------------

export type PaymentSourceKind =
  | 'bank'
  | 'card'
  | 'swish'
  | 'cash'
  | 'owner'
  | 'supplier_credit'
  | 'customer_credit'
  | 'tax_account';

export interface SavedColumnMapping {
  headerFingerprint: string;
  date: number;
  transactionDate?: number;
  amount?: number;
  debit?: number;
  credit?: number;
  balance?: number;
  text: number[];
  decimal: ',' | '.';
  newestFirst: boolean;
}

export interface PaymentSource {
  id: Uuid;
  companyId: Uuid;
  name: string;
  kind: PaymentSourceKind;
  /** 1930/1940/1910/2018|2893/2440/1510/1630 … */
  accountNumber: number;
  /** card/swish: the bank source they settle on. */
  linkedBankSourceId?: Uuid;
  /** 'swedbank' | 'seb' | … from import/bank/formats. */
  bankProfileId?: string;
  importMapping?: SavedColumnMapping;
  active: boolean;
  createdAt: IsoDateTime;
  /** Last edit (rename, hide, saved mapping); newest wins when two devices disagree. */
  updatedAt?: IsoDateTime;
}

// ---- Chart of accounts ------------------------------------------------------

/** BAS account classes 1-8. Class is always the first digit of the number. */
export type AccountClass = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

/**
 * Account type controls sign conventions in reports. BAS class decides it:
 * 1 asset, 2 liability/equity, 3 revenue, 4-8 expense (8 also holds financial
 * income; the BAS reference flags those explicitly).
 */
export type AccountType = 'asset' | 'liability' | 'equity' | 'revenue' | 'expense';

/** Where a company account came from. */
export type AccountOrigin = 'starter' | 'bas' | 'custom';

/**
 * A company's own account. One row per (companyId, number). The BAS reference
 * table is *not* the company's accounts; rows here are what can be booked on.
 */
export interface Account {
  id: Uuid;
  companyId: Uuid;
  number: number;
  name: string;
  /** SRU code (Skatteverket's field code). Required for every account. */
  sruCode: string;
  type: AccountType;
  origin: AccountOrigin;
  active: boolean;
  createdAt: IsoDateTime;
}

export type AccountEventKind =
  | 'activated' // BAS account activated for the company
  | 'created' // custom account created in a free BAS number
  | 'deactivated'
  | 'renamed' // name or SRU change, effective from a new fiscal year
  | 'closed_fiscal_year'
  | 'period_locked';

export interface AccountEvent {
  id: Uuid;
  companyId: Uuid;
  /** Null for events that are not about one account (e.g. closing a fiscal year). */
  accountNumber: number | null;
  kind: AccountEventKind;
  /** Fiscal year the event takes effect in. */
  fiscalYearId: Uuid;
  at: IsoDateTime;
  /** Free-form details for audit, e.g. previous/next name. */
  details: Record<string, string | number | boolean | null>;
}

// ---- Plain-language transaction model ---------------------------------------

export type TransactionKind =
  | 'purchase'
  | 'sale'
  | 'transfer'
  | 'owner_in'
  | 'owner_out'
  | 'refund'
  | 'tax'
  | 'settle'
  | 'vat_settlement'
  | 'year_end'
  | 'other';

export type TaxSubkind = 'vat_payment' | 'f_tax' | 'skattekonto_deposit';
export type VatRate = 25 | 12 | 6 | 0;
export type VatMode = 'domestic' | 'none' | 'rc_services_eu' | 'rc_services_non_eu' | 'rc_goods_eu';
export type Momskod =
  | '05' | '06' | '07' | '08'
  | '10' | '11' | '12'
  | '20' | '21' | '22' | '23' | '24'
  | '30' | '31' | '32'
  | '35' | '36' | '39'
  | '40' | '41' | '42'
  | '48' | '50'
  | '60' | '61' | '62';

export interface VatChoice {
  rate: VatRate;
  mode: VatMode;
  /** Printed VAT amount if known (receipt), otherwise computed from gross. */
  vatOre?: Ore;
  momskod?: Momskod;
}

export type ProposalSource = 'receipt' | 'bank' | 'manual' | 'generated';
export type PrefillReason = 'rule' | 'match' | 'memory' | 'suggestion' | null;

/**
 * Plain-language origin of a verifikation. Written ONCE by `bookVerifikation`
 * together with the rows and never updated (verifikationer are append-only).
 */
export interface VerifikationMeta {
  kind: TransactionKind;
  taxSubkind?: TaxSubkind;
  categoryId: string | null;
  paymentSourceId: Uuid | null;
  /** Transfers: the receiving source. */
  counterpartySourceId?: Uuid;
  vat?: VatChoice;
  counterparty: string;
  merchantKey?: string;
  source: ProposalSource;
  reason: PrefillReason;
  confidence?: number;
  inboxItemId?: Uuid;
  bankRowIds?: Uuid[];
  settlesVerifikationId?: Uuid;
  missingDocument: boolean;
  /** 'YYYY-MM' | 'YYYY-Qn' | 'FY:<fiscalYearId>' (see src/domain/periods/keys.ts). */
  periodKey: string;
}

/** The three series the app itself books in. Other strings are tolerated for imports. */
export type VerifikationSeries = 'A' | 'IB' | 'B';
export const VERIFIKATION_SERIES: readonly VerifikationSeries[] = ['A', 'IB', 'B'];

export interface VerifikationRow {
  accountNumber: number;
  debit: Ore;
  credit: Ore;
  description?: string;
}

/**
 * Immutable once written. Never update or delete; book a correcting
 * verifikation instead (see `correctsId`).
 */
export interface Verifikation {
  id: Uuid;
  companyId: Uuid;
  fiscalYearId: Uuid;
  /** Verifikation series: 'A' (default), 'IB' (opening balances), 'B' (year-end). */
  series: string;
  /** Sequential number within (fiscalYearId, series), assigned at booking. */
  number: number;
  /** Transaction date (bokföringsdatum). */
  date: IsoDate;
  description: string;
  rows: VerifikationRow[];
  /** Set when this verifikation corrects an earlier one. */
  correctsId?: Uuid;
  /** Optional reference to an attached document (Drive file id or local blob id). */
  attachmentIds: string[];
  bookedAt: IsoDateTime;
  /**
   * The device (browser profile) that booked it, from `settings.device`.
   * Provenance of the write, like `bookedAt`, and immutable with the rest:
   * the id only, never the name, because a device can be renamed and a name
   * frozen into an append-only record would go stale. Resolve it through the
   * `devices` registry. Absent on verifikationer booked before v6 and in
   * tests that never established an identity.
   */
  deviceId?: Uuid;
  /** Plain-language origin; absent for verifikationer entered in the manual editor. */
  meta?: VerifikationMeta;
}

// ---- Inbox & attachments ----------------------------------------------------

export type InboxItemKind = 'receipt' | 'bank_row';
export type InboxStatus =
  | 'new'
  | 'processing'
  | 'ready'
  | 'check'
  | 'needs_you'
  | 'booked'
  | 'skipped'
  | 'private'
  | 'error';
export type InboxTier = 'ready' | 'check' | 'needs_you';
export type InboxFlag =
  | 'possible_private'
  | 'foreign_currency'
  | 'no_vat_breakdown'
  | 'representation'
  | 'asset_candidate'
  | 'foreign_supplier'
  | 'duplicate'
  | 'unreadable'
  | 'locked_period'
  | 'outside_fiscal_year'
  | 'large_amount';
export type InboxSource = 'upload' | 'phone' | 'share' | 'drive' | 'picker' | 'bank';

export interface ReceiptReading {
  documentType: 'kvitto' | 'faktura' | 'kontoutdrag' | 'annat';
  merchant: string | null;
  orgNr: string | null;
  date: IsoDate | null;
  currency: string;
  totalOre: Ore | null;
  vatLines: { rate: VatRate; baseOre: Ore | null; amountOre: Ore }[];
  paymentHint: 'card' | 'cash' | 'swish' | 'invoice' | 'unknown';
  readable: boolean;
  uncertainFields: string[];
  summary: string;
  /**
   * The text printed on the document, as read, for free-text search only
   * (“hitta köpet där det stod kaffefilter”). Never acted on and never shown
   * as fact: nothing is booked from it, so an imperfect line costs at most a
   * false hit in the search. Absent on readings made before the field
   * existed and on documents with no legible text.
   */
  printedText?: string;
}

export interface ProposalQuestion {
  /** `match`: which payment or purchase the document belongs to (option values are ids, or `none`). */
  field: 'category' | 'vat' | 'payment' | 'kind' | 'persons' | 'match';
  text: string;
  options: { label: string; value: string }[];
}

/**
 * One explicit allocation of a payment or a credit to a booked verifikation:
 * which invoice, utlägg or purchase it settles or refunds, and how much of it.
 * A `settle` may carry several (one payment for two invoices); a `refund`
 * carries the purchase it mirrors. Amounts are positive öre and never exceed
 * what is outstanding on the target when booked (checked again in the
 * booking transaction).
 */
export interface ProposalAllocation {
  verifikationId: Uuid;
  amountOre: Ore;
}

/** Answers the review card collects that are not fields of the sentence. */
export interface ProposalOptions {
  /** "Faktura – betalas senare". */
  deferPayment?: boolean;
  /** Representation: number of persons. */
  persons?: number;
  /** The half-prisbasbelopp rule overruled either way. */
  assetChoice?: 'expense' | 'asset';
}

export interface Proposal {
  kind: TransactionKind;
  categoryId: string | null;
  paymentSourceId: Uuid | null;
  vat: VatChoice | null;
  amountOre: Ore | null;
  date: IsoDate | null;
  counterparty: string;
  reason: PrefillReason;
  confidence: number;
  question?: ProposalQuestion;
  /** `settle`/`refund`: what the money settles or refunds, with explicit amounts. */
  allocations?: ProposalAllocation[];
  /** Card answers kept with the proposal so a reload and the batch see them. */
  options?: ProposalOptions;
}

/**
 * The fields of a proposal the user has set — by an answer, an edit or a
 * confirmed match. A reconciliation rebuilds everything else from the
 * evidence and never overwrites these.
 */
export type DecidedField =
  | 'kind'
  | 'categoryId'
  | 'paymentSourceId'
  | 'vat'
  | 'amountOre'
  | 'date'
  | 'counterparty'
  | 'taxSubkind'
  | 'options'
  /** The bank row(s) a receipt was matched to are confirmed, not provisional. */
  | 'match'
  /** The allocations of a settlement or refund are the user's choice. */
  | 'allocations';

/**
 * What the AI read off a receipt, kept as returned so a later reconciliation
 * (a bank import, an answer, a booking elsewhere) can rebuild the proposal
 * without a second call. `reading` on the item is the document's facts; this
 * is the reader's interpretation of them.
 */
export interface ReceiptExtraction {
  at: IsoDateTime;
  proposal: Pick<Proposal, 'kind' | 'categoryId' | 'vat' | 'confidence' | 'question'>;
  flags: InboxFlag[];
}

/** The AI's classification of a bank row, kept for the same reason. */
export interface BankRowClassification {
  at: IsoDateTime;
  kind: TransactionKind;
  categoryId: string | null;
  vat: VatChoice | null;
  confidence: number;
  counterparty: string;
  needsReceipt: boolean;
}

export interface InboxItem {
  id: Uuid;
  companyId: Uuid;
  kind: InboxItemKind;
  status: InboxStatus;
  tier?: InboxTier;
  tierReasons: string[];
  flags: InboxFlag[];
  attachmentId?: Uuid;
  driveFileId?: string;
  driveMd5?: string;
  source: InboxSource;
  reading?: ReceiptReading;
  proposal?: Proposal;
  bankRowId?: Uuid;
  /**
   * The bank rows this receipt is paired with. Provisional until `decided`
   * holds `'match'` or the item is booked: a reconciliation may reassign a
   * provisional row to a better candidate, never a confirmed one.
   */
  matchedBankRowIds: Uuid[];
  duplicateOfVerifikationId?: Uuid;
  /** The earlier receipt in Att göra this one looks like a duplicate of (two documents for one purchase, or the same document twice). */
  duplicateOfItemId?: Uuid;
  verifikationId?: Uuid;
  /** Every verifikation one item booked, when it booked more than one (a payment settling two invoices). */
  verifikationIds?: Uuid[];
  error?: string;
  /** The provider's own wording behind `error`, for "Tekniska detaljer". Never a key. */
  errorTechnical?: string;
  note?: string;
  /**
   * Which handover added the item (one upload, one import). Provenance and a
   * filter for "det jag just lade till" — never an accounting boundary: an
   * item matches evidence from any batch and keeps the date of its document.
   */
  batchId?: string;
  /** The AI's reading, kept so a reconciliation never asks twice. */
  extraction?: ReceiptExtraction;
  /** The AI's classification of the bank row, kept for the same reason. */
  classification?: BankRowClassification;
  /** Proposal fields the user decided; a reconciliation leaves them alone. */
  decided?: DecidedField[];
  /**
   * Matches the user said no to: bank row ids for a receipt, verifikation ids
   * for a bank row. Never proposed again; their allocations were released.
   */
  rejectedMatches?: Uuid[];
  /** A second document for the same event (a card slip next to the receipt): the item it was folded into. */
  mergedIntoItemId?: Uuid;
  /** Documents folded into this item; booked alongside its own attachment. */
  extraAttachmentIds?: Uuid[];
  createdAt: IsoDateTime;
  updatedAt: IsoDateTime;
}

export interface Attachment {
  id: Uuid;
  companyId: Uuid;
  name: string;
  mediaType: string;
  bytes: number;
  /** Original (≤ 2000 px JPEG or PDF); null until a restore has fetched it back. */
  blob: Blob | null;
  /**
   * Mirrors `blob !== null`, because finding the attachments that still need
   * their bytes must not load every receipt in the company to look at `blob`.
   * Maintained by the attachment service, the restore and the hydration.
   */
  hasLocalBlob: 0 | 1;
  thumbnail: Blob;
  driveFileId?: string;
  sha256: string;
  createdAt: IsoDateTime;
}

// ---- Bank -------------------------------------------------------------------

export type BankImportVia = 'csv' | 'html' | 'pdf-text' | 'pdf-ai';

export interface BankStatement {
  id: Uuid;
  companyId: Uuid;
  paymentSourceId: Uuid;
  fileName: string;
  bankProfileId?: string;
  importedAt: IsoDateTime;
  rowCount: number;
  duplicates: number;
  openingBalanceOre?: Ore;
  closingBalanceOre?: Ore;
  from: IsoDate;
  to: IsoDate;
  driveFileId?: string;
  via: BankImportVia;
}

/** Files preserved locally alongside the database, using the shared company folder layout. */
export interface ArchiveFile {
  id: string;
  companyId: Uuid;
  path: string;
  blob: Blob;
  mediaType: string;
  /** A source statement, when this is its original file. */
  statementId?: Uuid;
}

export type BankRowStatus = 'open' | 'linked' | 'booked';

export interface BankRow {
  id: Uuid;
  companyId: Uuid;
  statementId: Uuid;
  paymentSourceId: Uuid;
  /** SHA-256 over account, date, amount, text, balance and occurrence index. Unique. */
  fingerprint: string;
  /** Bank-native id when present (SEB Verifikationsnummer, Revolut ID). */
  bankId?: string;
  bookingDate: IsoDate;
  transactionDate?: IsoDate;
  amountOre: Ore;
  balanceOre?: Ore;
  text: string;
  raw: string;
  status: BankRowStatus;
  linkedVerifikationIds: Uuid[];
  inboxItemId?: Uuid;
}

// ---- Merchant memory --------------------------------------------------------

export interface MerchantRule {
  id: Uuid;
  companyId: Uuid;
  merchantKey: string;
  displayName: string;
  kind: TransactionKind;
  categoryId: string;
  vat: VatChoice;
  paymentSourceId: Uuid | null;
  /** Total confirmations for this merchant, across every category. */
  timesUsed: number;
  /**
   * How often each category has been confirmed here. A shop that sells both
   * tea cups and printers has two entries, and memory then stops asserting a
   * category at all — see `isAmbiguousCategory`. Absent on rules written
   * before the field existed; `timesUsed` stands in for the single category.
   */
  categoryCounts?: Record<string, number>;
  lastUsedAt: IsoDateTime;
  createdFrom: 'user' | 'ai_confirmed';
  createdAt: IsoDateTime;
}

// ---- Periods / locks (append-only) -------------------------------------------

export type PeriodLockReason = 'vat_declared' | 'year_closed' | 'manual';

export interface PeriodLock {
  id: Uuid;
  companyId: Uuid;
  fiscalYearId: Uuid;
  periodKey: string;
  /** Every date <= lockedThrough refuses new verifikationer (IB/B exemptions in rules). */
  lockedThrough: IsoDate;
  reason: PeriodLockReason;
  settlementVerifikationId?: Uuid;
  at: IsoDateTime;
}

// ---- Drive state ------------------------------------------------------------

export interface DriveFolders {
  root: string;
  company: string;
  inbox: string;
  kvitton: string;
  ejBokfort: string;
  kontoutdrag: string;
  arkiv: string;
  bokslut: string;
}

/**
 * Two devices booked the same verifikation number for the same year and
 * series, and neither copy can be rewritten (verifikationer are append-only).
 * Set by the ledger sync when a pull finds the collision; nothing is merged
 * and nothing is pushed until the user decides which copy stands.
 */
export interface LedgerConflict {
  at: IsoDateTime;
  /** Swedish sentence for the UI. */
  message: string;
  /** "A13", "B2" … — the colliding numbers, for the message and the log. */
  numbers: string[];
}

export interface DriveState {
  /** Equal to `companyId`. */
  id: Uuid;
  companyId: Uuid;
  folders: DriveFolders | null;
  snapshotFileId?: string;
  lastBackupAt?: IsoDateTime;
  /**
   * The remote snapshot's opaque `version` that the local copy *includes*:
   * either this device wrote it, or it pulled and merged it. A remote entry
   * with a different version was written by another device and has to be
   * merged before anything is pushed (`src/cloud/sync/ledger.ts`). The name
   * predates the merge; it is not a timestamp and is only compared for
   * equality.
   */
  lastBackupModifiedTime?: string;
  pendingChanges: number;
  dirtySince?: IsoDateTime;
  needsSignIn: boolean;
  lastError?: string;
  lastInboxScanAt?: IsoDateTime;
  /** 'YYYY-MM' of the latest monthly frozen copy. */
  lastMonthlyFrozen?: string;
  /** When the remote snapshot was last checked for another device's changes. */
  lastPulledAt?: IsoDateTime;
  /** Present while a numbering collision with another device is unresolved. */
  ledgerConflict?: LedgerConflict;
}

export type BackupKind = 'auto' | 'manual' | 'monthly' | 'year_end';

export interface BackupLogEntry {
  id: Uuid;
  companyId: Uuid;
  at: IsoDateTime;
  kind: BackupKind;
  ok: boolean;
  bytes?: number;
  fileId?: string;
  error?: string;
}

export type UploadQueueStatus = 'pending' | 'uploading' | 'done' | 'failed';

export interface UploadQueueItem {
  id: Uuid;
  companyId?: Uuid;
  folderId: string;
  name: string;
  blob: Blob;
  mediaType: string;
  appProperties: Record<string, string>;
  attempts: number;
  lastError?: string;
  createdAt: IsoDateTime;
  status: UploadQueueStatus;
}

// ---- Settings (single row) --------------------------------------------------

export type AiProviderKind = 'gemini' | 'openai' | 'openai-compatible';
export type GeminiPlan = 'free' | 'paid';
export type AiParallelism = 1 | 2 | 3;

export interface AiSettings {
  provider: AiProviderKind;
  /** Only used for `openai-compatible`; e.g. http://localhost:11434/v1 */
  baseUrl: string;
  apiKey: string;
  model: string;
  /** Drives pacing (free: sequential + backoff; paid: parallel) and 429 copy. */
  geminiPlan: GeminiPlan;
  /**
   * Always true. New receipts are read as soon as they arrive on every path;
   * the toggle that once set this was removed in 2026-09. Kept in the schema
   * so stored settings rows and backups stay readable.
   */
  autoReadInbox: boolean;
  parallel: AiParallelism;
}

export type UiTheme = 'system' | 'light' | 'dark';

export interface UiSettings {
  advancedMode: boolean;
  theme: UiTheme;
  dismissedCallouts: string[];
}

export interface OnboardingState {
  /** 0 = not started; 5 = finished (/start/klart). */
  step: number;
  companyId?: Uuid;
}

/** Which cloud backend the RemoteStore port is pointed at. One at a time. */
export type CloudProvider = 'drive' | 'koofr';

/**
 * Koofr credentials. An app password, never the account password: the user
 * generates a named one in Koofr and can revoke it there without touching
 * their account. Stored in IndexedDB and sent only to `baseUrl`, the same
 * contract as the AI provider key.
 */
export interface KoofrSettings {
  /** Koofr host; overridable for the rcs-rds.ro tenant. */
  baseUrl: string;
  email: string;
  appPassword: string;
  /** The primary mount, resolved from /api/v2/mounts when connecting. */
  mountId: string;
}


export interface CloudSettings {
  provider: CloudProvider;
  koofr: KoofrSettings;
}

/**
 * This installation's identity: one per browser profile, minted on first run
 * and never restored from a backup (a restored copy is a different device).
 * `name` is what the user calls it; it is local and editable, so nothing
 * durable may copy it — store `id` and resolve the name through `devices`.
 */
export interface DeviceIdentity {
  id: Uuid;
  name: string;
  createdAt: IsoDateTime;
}

/**
 * A device known to one company's bookkeeping. Working state, not the legal
 * record: it travels in the backup so a `Verifikation.deviceId` written on
 * another machine still has a name after a restore, and the user may rename
 * or remove rows without touching a verifikation.
 */
export interface DeviceRegistration {
  /** `${companyId}:${deviceId}`, so re-registering is an idempotent put. */
  id: string;
  companyId: Uuid;
  deviceId: Uuid;
  /** The device's name as it was when last seen from that device. */
  name: string;
  firstSeen: IsoDate;
  lastSeen: IsoDate;
}

export interface Settings {
  /** Single-row table, id is always "settings". */
  id: 'settings';
  activeCompanyId: Uuid | null;
  /** Null until the first run establishes one; see `src/domain/devices/`. */
  device: DeviceIdentity | null;
  ai: AiSettings;
  cloud: CloudSettings;
  ui: UiSettings;
  onboarding: OnboardingState;
}
